/**
 * Pay Summary Page
 * Shows pilots their monthly pay breakdown
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getMonthlySummary, MonthlySummary } from '@/services/payService';
import { Card, CardHeader, CardTitle, CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  BanknotesIcon,
  ClockIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';

export default function PaySummaryPage() {
  const { user } = useAuth();
  const [summary, setSummary] = useState<MonthlySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Default to current month
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  useEffect(() => {
    loadSummary();
  }, [user, year, month]);

  const loadSummary = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      const data = await getMonthlySummary(user.id, year, month);
      setSummary(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load pay summary');
    } finally {
      setLoading(false);
    }
  };

  const goToPreviousMonth = () => {
    if (month === 1) {
      setMonth(12);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };

  const goToNextMonth = () => {
    if (month === 12) {
      setMonth(1);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    const [y, m, d] = dateStr.slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-CA', {
      month: 'short',
      day: 'numeric',
    });
  };

  const monthName = new Date(year, month - 1).toLocaleDateString('en-CA', {
    month: 'long',
    year: 'numeric',
  });

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card variant="elevated">
          <CardBody>
            <p className="text-danger text-center">{error}</p>
            <Button onClick={loadSummary} className="mt-4 mx-auto block">
              Retry
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      {/* Header with Month Navigation */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-foreground">Pay Summary</h1>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={goToPreviousMonth}>
            <ChevronLeftIcon className="w-5 h-5" />
          </Button>
          <span className="text-lg font-medium min-w-[160px] text-center">
            {monthName}
          </span>
          <Button variant="ghost" size="sm" onClick={goToNextMonth}>
            <ChevronRightIcon className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Total Pay Card */}
      <Card variant="elevated" className="mb-6 bg-primary text-primary-foreground">
        <CardBody className="text-center py-8">
          <p className="text-sm opacity-80 mb-1">Total Pay</p>
          <p className="text-4xl font-bold">
            {formatCurrency(summary?.totalPay || 0)}
          </p>
          <p className="text-sm opacity-80 mt-2">
            {summary?.totalFlights || 0} flights | {summary?.totalHours || 0} hours
          </p>
        </CardBody>
      </Card>

      {/* Pay Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Regular Pay */}
        <Card>
          <CardBody>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-green-100 dark:bg-green-900/30">
                <ClockIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Regular</p>
                <p className="text-xl font-semibold">
                  {formatCurrency(summary?.regularPay || 0)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {summary?.regularHours || 0} hrs
                </p>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Overtime Pay */}
        <Card>
          <CardBody>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/30">
                <BanknotesIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Overtime</p>
                <p className="text-xl font-semibold">
                  {formatCurrency(summary?.overtimePay || 0)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {summary?.overtimeHours || 0} hrs (after 30)
                </p>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Short Notice Pay */}
        <Card>
          <CardBody>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-yellow-100 dark:bg-yellow-900/30">
                <BoltIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Short Notice</p>
                <p className="text-xl font-semibold">
                  {formatCurrency(summary?.shortNoticePay || 0)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {summary?.shortNoticeHours || 0} hrs
                </p>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Flight List */}
      <Card variant="elevated">
        <CardHeader>
          <CardTitle>Flights This Month</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {summary?.flights && summary.flights.length > 0 ? (
            <div className="divide-y divide-border">
              {summary.flights.map((flight: any) => {
                const flightDate = flight.flightDate || flight.date;
                const airTime = flight.airTime ?? flight.actualAirTime ?? 0;
                const route = [flight.fromAirport, flight.toAirport].filter(Boolean).join(' → ');

                return (
                  <div
                    key={flight.id}
                    className="p-4 flex items-center justify-between hover:bg-muted/50"
                  >
                    <div className="flex items-center gap-4">
                      <div className="text-center min-w-[50px]">
                        <p className="text-sm font-medium">
                          {formatDate(flightDate)}
                        </p>
                      </div>
                      <div>
                        <p className="font-medium">
                          {flight.qNumber}
                          {flight.isShortNotice && (
                            <span className="ml-2 px-2 py-0.5 text-xs bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-400 rounded">
                              Short Notice
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {flight.tailNumber}{route ? ` | ${route}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{airTime} hrs</p>
                      <p className="text-xs text-muted-foreground">air time</p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              No flights recorded for this month
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
